import React, { useEffect, useState, useRef } from 'react';
import { BellIcon, HelpCircleIcon, Check, Sparkles } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
export interface NavActionsProps {
  'data-id'?: string;
}
interface Notification {
  id: string;
  category: 'vip' | 'loyalty' | 'engagement' | 'system';
  message: string;
  time: string;
  unread: boolean;
}
const sampleNotifications: Notification[] = [
// VIP Alerts
{
  id: '1',
  category: 'vip',
  message: 'VIP Alert: Michael Torres skipped their December box',
  time: '5m ago',
  unread: true
}, {
  id: '2',
  category: 'vip',
  message: 'VIP Alert: Amanda Chen cancelled - Reason: Moving overseas',
  time: '12m ago',
  unread: true
}, {
  id: '3',
  category: 'vip',
  message: 'Top subscriber Jennifer Walsh swapped 3 products',
  time: '1h ago',
  unread: false
}, {
  id: '4',
  category: 'vip',
  message: 'High-value: Robert Kim paused - 18 month subscriber',
  time: '2h ago',
  unread: false
}, {
  id: '5',
  category: 'vip',
  message: 'VIP payment failed: Diana Patel ($289/month)',
  time: '3h ago',
  unread: true
},
// Loyalty & Rewards
{
  id: '6',
  category: 'loyalty',
  message: 'Sarah Mitchell unlocked Gold tier - 500 points reached',
  time: '4h ago',
  unread: false
}, {
  id: '7',
  category: 'loyalty',
  message: 'James Cooper redeemed reward: Free Shipping for Life',
  time: '5h ago',
  unread: false
}, {
  id: '8',
  category: 'loyalty',
  message: 'Emily Park is 20 points from Platinum status',
  time: '6h ago',
  unread: false
}, {
  id: '9',
  category: 'loyalty',
  message: 'Marcus Webb earned 50 bonus points - 6 month milestone',
  time: '8h ago',
  unread: false
}, {
  id: '10',
  category: 'loyalty',
  message: 'New reward redemption: Lisa Chen claimed Free Wine Tote',
  time: '1d ago',
  unread: false
},
// Engagement Signals
{
  id: '11',
  category: 'engagement',
  message: 'Win-back opportunity: David Kim logged in after 3 months inactive',
  time: '1d ago',
  unread: false
}, {
  id: '12',
  category: 'engagement',
  message: 'High engagement: Anna Wilson browsing upgrade options',
  time: '2d ago',
  unread: false
}, {
  id: '13',
  category: 'engagement',
  message: 'At-risk VIP: No portal login in 60 days - Jennifer Adams',
  time: '2d ago',
  unread: false
},
// System/Admin
{
  id: '14',
  category: 'system',
  message: 'Scheduled maintenance: Sync paused 2am-4am AEST',
  time: '3d ago',
  unread: false
}, {
  id: '15',
  category: 'system',
  message: 'API rate limit warning - 80% capacity reached',
  time: '3d ago',
  unread: false
}, {
  id: '16',
  category: 'system',
  message: 'New Shopify app update available',
  time: '4d ago',
  unread: false
}];
type FilterCategory = 'all' | 'vip' | 'loyalty' | 'engagement' | 'system';
const filterLabels: Record<FilterCategory, string> = {
  all: 'All',
  vip: 'VIP',
  loyalty: 'Loyalty',
  engagement: 'Engagement',
  system: 'System'
};
const categoryLabels = {
  vip: 'VIP Alerts',
  loyalty: 'Loyalty & Rewards',
  engagement: 'Engagement Signals',
  system: 'System & Admin'
};
export function NavActions({
  'data-id': dataId
}: NavActionsProps) {
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState(sampleNotifications);
  const [activeFilter, setActiveFilter] = useState<FilterCategory>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const filteredNotifications = activeFilter === 'all' ? notifications : notifications.filter(n => n.category === activeFilter);
  const unreadCount = notifications.filter(n => n.unread).length;
  const filteredUnreadCount = filteredNotifications.filter(n => n.unread).length;
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && buttonRef.current && !dropdownRef.current.contains(event.target as Node) && !buttonRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    if (isNotificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationsOpen]);
  const handleMarkAllRead = () => {
    if (activeFilter === 'all') {
      setNotifications(notifications.map(n => ({
        ...n,
        unread: false
      })));
    } else {
      setNotifications(notifications.map(n => n.category === activeFilter ? {
        ...n,
        unread: false
      } : n));
    }
  };
  const groupedNotifications = filteredNotifications.reduce((acc, notification) => {
    if (!acc[notification.category]) {
      acc[notification.category] = [];
    }
    acc[notification.category].push(notification);
    return acc;
  }, {} as Record<string, Notification[]>);
  return <div className="flex items-center gap-2" data-id="element-3729" data-mp="37dt7zqmw1p7k">
      <Button size="sm" iconLeft={<Sparkles size={16} className="text-[#6F42C1]" data-id="element-3731" data-mp="37f4g22nd9j4h" />} className="bg-white text-[#6F42C1] border border-[#6F42C1] hover:bg-[#F8F5FC]" onClick={() => console.log('Ask WinePik clicked')} data-id="element-3730" data-mp="37el6b97ve11j">
        <span className="text-[#6F42C1]" data-id="element-3732" data-mp="qz7ywkq14c91">Ask WinePik</span>
      </Button>

      <div className="relative" data-id="element-3733" data-mp="14q33hkthw76t">
        <button ref={buttonRef} onClick={() => setIsNotificationsOpen(!isNotificationsOpen)} className="
            p-2 rounded-md text-[#475569] 
            hover:text-[#0EA5E9] hover:bg-[#F1F5F9]
            transition-all duration-200 ease-in-out
            min-h-[48px] min-w-[48px] flex items-center justify-center
            focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
            relative
          " aria-label="Notifications" aria-expanded={isNotificationsOpen} data-id="element-3734" data-mp="14qcwzidr0wh9">
          <BellIcon size={20} data-id="element-3735" data-mp="h3o4k2r45lme" />
          {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#EF4444] rounded-full" data-id="element-3736" data-mp="1xes65oa78kko"></span>}
        </button>

        {isNotificationsOpen && <div ref={dropdownRef} className="
              absolute right-0 top-full mt-2 w-[420px] 
              bg-white rounded-lg shadow-lg border border-[#CBD5E1]
              z-50 overflow-hidden
              animate-in fade-in slide-in-from-top-2 duration-200
            " data-id="element-3737" data-mp="1sitbxk26w8cc">





            {/* Header */}
            <div className="p-4 border-b border-[#CBD5E1]" data-id="element-3738" data-mp="z260fnnne71c">
              <div className="flex items-center justify-between mb-3" data-id="element-3739" data-mp="142k12kmx6epc">
                <div data-id="element-3740" data-mp="7dm7h5qwomtr">
                  <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-3741" data-mp="7e4e8wb72jfx">
                    Notifications
                  </h3>
                  {filteredUnreadCount > 0 && <p className="text-sm text-[#94A3B8]" data-id="element-3742" data-mp="gdrdbbggbd5t">
                      {filteredUnreadCount} unread
                    </p>}
                </div>
                {filteredUnreadCount > 0 && <Button onClick={handleMarkAllRead} variant="ghost" size="sm" iconLeft={<Check size={16} data-id="element-3744" data-mp="3jty4wjlldjy8" />} className="text-sm font-medium" data-id="element-3743" data-mp="l62aw7npymv7">
                    Mark all read
                  </Button>}
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-1 overflow-x-auto" data-id="element-3745" data-mp="3h8gvm7nvwdog">
                {(Object.keys(filterLabels) as FilterCategory[]).map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`
                      px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap
                      transition-all duration-200 ease-in-out
                      focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
                      ${activeFilter === filter ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}
                    `} data-id="element-3746" data-mp="3c8m72arl006k">
                      {filterLabels[filter]}
                    </button>)}
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-[500px] overflow-y-auto" data-id="element-3747" data-mp="2fkg6q01ozty3">
              {Object.keys(groupedNotifications).length === 0 ? <div className="px-4 py-12 text-center" data-id="element-3748" data-mp="2alld0326skz7">
                  <p className="text-[#94A3B8]" data-id="element-3749" data-mp="2am4who7hjhfc">No notifications</p>
                </div> : Object.entries(groupedNotifications).map(([category, items]) => <div key={category} className="border-b border-[#E2E8F0] last:border-b-0" data-id="element-3750" data-mp="7bhuapaekvah">
                      {activeFilter === 'all' && <div className="px-4 py-2 bg-[#F8FAFC]" data-id="element-3751" data-mp="3pgbcivamfg9f">
                          <h4 className="text-xs font-semibold text-[#475569] uppercase tracking-wide" data-id="element-3752" data-mp="3tiybrh9mg3gn">
                            {categoryLabels[category as keyof typeof categoryLabels]}
                          </h4>
                        </div>}
                      {items.map(notification => <div key={notification.id} className={`
                          px-4 py-3 hover:bg-[#F8FAFC] cursor-pointer
                          transition-colors duration-200 ease-in-out
                          border-l-2 ${notification.unread ? 'border-l-[#0EA5E9] bg-[#F0F9FF]' : 'border-l-transparent'}
                        `} data-id="element-3753" data-mp="3kkcv5rnpmyii">
                          <div className="flex items-start justify-between gap-3" data-id="element-3754" data-mp="31s1ywgnk9ytd">
                            <p className={`text-sm leading-relaxed ${notification.unread ? 'text-[#0F172A] font-medium' : 'text-[#475569]'}`} data-id="element-3755" data-mp="2i5gumdcsfq6d">
                              {notification.message}
                            </p>
                            {notification.unread && <span className="flex-shrink-0 w-2 h-2 mt-1.5 bg-[#0EA5E9] rounded-full" data-id="element-3756" data-mp="2r7zvirx0jspz"></span>}
                          </div>
                          <p className="text-xs text-[#94A3B8] mt-1" data-id="element-3757" data-mp="314kx139tqlp2">
                            {notification.time}
                          </p>
                        </div>)}
                    </div>)}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-[#CBD5E1] bg-[#F8FAFC]" data-id="element-3758" data-mp="3au4kkm8hf3q9">
              <button className="
                  w-full py-2 text-sm font-medium text-[#0EA5E9]
                  hover:bg-white rounded-md
                  transition-all duration-200 ease-in-out
                  focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
                " data-id="element-3759" data-mp="35vb54o9mx2ng">





                View all notifications
              </button>
            </div>
          </div>}
      </div>

      <button className="
          p-2 rounded-md text-[#475569] 
          hover:text-[#0EA5E9] hover:bg-[#F1F5F9]
          transition-all duration-200 ease-in-out
          min-h-[48px] min-w-[48px] flex items-center justify-center
          focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20
        " aria-label="Product tours and help" data-id="element-3760" data-mp="gr1tp7klvpiz">
        <HelpCircleIcon size={20} data-id="element-3761" data-mp="ziumb0vrj8q6" />
      </button>
    </div>;
}